import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { InputComponent } from '@app/components/input/input.component';
import { CheckboxComponent } from '@app/components/checkbox/checkbox.component';
import { Loading } from '@app/components/ui/loading/loading';
import { TagSelectorComponent } from '@app/components/tag-selector/tag-selector.component';
import { GeoImageCard, isGeoTiffFile } from '../geo-image-upload.models';

@Component({
  selector: 'app-geo-image-upload-card',
  standalone: true,
  templateUrl: './geo-image-upload-card.component.html',
  styleUrls: ['./geo-image-upload-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, TranslateModule, SecondaryButton, InputComponent, CheckboxComponent, Loading, TagSelectorComponent]
})
export class GeoImageUploadCardComponent {
  @Input() card!: GeoImageCard;
  @Input() showApplyAll = false;

  @Output() remove = new EventEmitter<void>();
  @Output() applyAll = new EventEmitter<void>();
  @Output() cardChange = new EventEmitter<GeoImageCard>();
  @Output() enterCoordinates = new EventEmitter<void>();

  onTitleChange(title: string): void {
    this.card.title = title;
    this.cardChange.emit(this.card);
  }

  onTagIdsChange(tagIds: string[]): void {
    this.card.tagIds = tagIds;
    this.cardChange.emit(this.card);
  }

  onSelectedChange(selected: boolean): void {
    this.card.selected = selected;
    this.cardChange.emit(this.card);
  }

  handleRemove(event: Event): void {
    event.stopPropagation();
    this.remove.emit();
  }

  /** A GeoTIFF has no browser-renderable preview until the server generates one after upload
   * (`GenerateRasterPreviewCommand`) — `URL.createObjectURL` on it just yields a broken `<img>`. */
  get isTiffPreview(): boolean {
    return isGeoTiffFile(this.card.file);
  }

  /** Only an accepted `Photo` with coordinates, or an accepted `Raster`, can be checked (FR-004/FR-005). */
  get canBeSelected(): boolean {
    if (this.card.isChecking || this.card.rejectionReason) {
      return false;
    }
    return this.card.imageKind === 'Raster' || (this.card.imageKind === 'Photo' && this.card.coordinates != null);
  }

  get needsManualCoordinates(): boolean {
    return !this.card.isChecking && !this.card.rejectionReason && this.card.imageKind === 'Photo' && this.card.coordinates == null;
  }

  get statusLabelKey(): string {
    if (this.card.isChecking) return 'UPLOADED_GEO_IMAGES.MODAL_STATUS_CHECKING';
    if (this.card.rejectionReason) return this.rejectionReasonKey;
    if (this.needsManualCoordinates) return 'UPLOADED_GEO_IMAGES.MODAL_STATUS_NEEDS_COORDINATES';
    return 'UPLOADED_GEO_IMAGES.MODAL_STATUS_ACCEPTED';
  }

  get rejectionReasonKey(): string {
    switch (this.card.rejectionReason) {
      case 'UnsupportedFormat':
        return 'UPLOADED_GEO_IMAGES.MODAL_ERROR_UNSUPPORTED_FORMAT';
      case 'UnreadableFile':
        return 'UPLOADED_GEO_IMAGES.MODAL_ERROR_UNREADABLE_FILE';
      case 'NoGeoreferencing':
      default:
        return this.card.imageKind === 'Raster'
          ? 'UPLOADED_GEO_IMAGES.MODAL_ERROR_RASTER_NO_GEOREFERENCE'
          : 'UPLOADED_GEO_IMAGES.MODAL_ERROR_NO_GEOREFERENCE';
    }
  }

  get formattedCoordinates(): string | null {
    if (!this.card.coordinates) {
      return null;
    }
    const { latitude, longitude } = this.card.coordinates;
    if (latitude == null || longitude == null || isNaN(latitude) || isNaN(longitude)) {
      return null;
    }
    return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
  }

  get formattedDate(): string {
    if (!this.card.captureDate) {
      return '';
    }
    const date = this.card.captureDate instanceof Date ? this.card.captureDate : new Date(this.card.captureDate);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  get formattedFileSize(): string {
    const mb = this.card.file.size / (1024 * 1024);
    return mb.toFixed(1);
  }
}
